import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

// Available themes with translations
export const THEMES = {
  light: { 
    name: 'Light', 
    icon: '☀️',
    translations: {
      EN: 'Light',
      KU: 'ڕووناک',
      AR: 'فاتح',
      TR: 'Açık'
    }
  },
  dark: { 
    name: 'Dark', 
    icon: '🌙',
    translations: {
      EN: 'Dark',
      KU: 'تاریک',
      AR: 'داكن',
      TR: 'Koyu'
    }
  },
  beige: { 
    name: 'Beige', 
    icon: '🟤',
    translations: {
      EN: 'Beige',
      KU: 'بێجی',
      AR: 'بيج',
      TR: 'Bej'
    }
  },
  blue: { 
    name: 'Blue', 
    icon: '🔵',
    translations: {
      EN: 'Blue',
      KU: 'شین',
      AR: 'أزرق',
      TR: 'Mavi'
    }
  },
  green: { 
    name: 'Green', 
    icon: '🟢',
    translations: {
      EN: 'Green',
      KU: 'سەوز',
      AR: 'أخضر',
      TR: 'Yeşil'
    }
  },
  purple: { 
    name: 'Purple', 
    icon: '🟣',
    translations: {
      EN: 'Purple',
      KU: 'مۆر',
      AR: 'بنفسجي',
      TR: 'Mor'
    }
  },
};

// Available primary colors with translations
export const PRIMARY_COLORS = {
  emerald: { 
    name: 'Emerald', 
    color: '#10b981', 
    hover: '#059669', 
    light: '#d1fae5', 
    dark: '#064e3b',
    translations: {
      EN: 'Emerald',
      KU: 'زەمەرەد',
      AR: 'زمردي',
      TR: 'Zümrüt'
    }
  },
  blue: { 
    name: 'Blue', 
    color: '#3b82f6', 
    hover: '#2563eb', 
    light: '#dbeafe', 
    dark: '#1e40af',
    translations: {
      EN: 'Blue',
      KU: 'شین',
      AR: 'أزرق',
      TR: 'Mavi'
    }
  },
  purple: { 
    name: 'Purple', 
    color: '#8b5cf6', 
    hover: '#7c3aed', 
    light: '#ede9fe', 
    dark: '#5b21b6',
    translations: {
      EN: 'Purple',
      KU: 'مۆر',
      AR: 'بنفسجي',
      TR: 'Mor'
    }
  },
  pink: { 
    name: 'Pink', 
    color: '#ec4899', 
    hover: '#db2777', 
    light: '#fce7f3', 
    dark: '#831843',
    translations: {
      EN: 'Pink',
      KU: 'پەمبەیی',
      AR: 'وردي',
      TR: 'Pembe'
    }
  },
  red: { 
    name: 'Red', 
    color: '#ef4444', 
    hover: '#dc2626', 
    light: '#fee2e2', 
    dark: '#991b1b',
    translations: {
      EN: 'Red',
      KU: 'سور',
      AR: 'أحمر',
      TR: 'Kırmızı'
    }
  },
  orange: { 
    name: 'Orange', 
    color: '#f59e0b', 
    hover: '#d97706', 
    light: '#fef3c7', 
    dark: '#92400e',
    translations: {
      EN: 'Orange',
      KU: 'نارەنجی',
      AR: 'برتقالي',
      TR: 'Turuncu'
    }
  },
  teal: { 
    name: 'Teal', 
    color: '#14b8a6', 
    hover: '#0d9488', 
    light: '#ccfbf1', 
    dark: '#134e4a',
    translations: {
      EN: 'Teal',
      KU: 'شینی کاڵ',
      AR: 'أزرق مخضر',
      TR: 'Teal'
    }
  },
  indigo: { 
    name: 'Indigo', 
    color: '#6366f1', 
    hover: '#4f46e5', 
    light: '#e0e7ff', 
    dark: '#312e81',
    translations: {
      EN: 'Indigo',
      KU: 'نیلی',
      AR: 'نيلي',
      TR: 'İndigo'
    }
  },
};

// Available fonts with translations
export const FONTS = {
  customFont1: {
    name: 'Font 1',
    family: "'Custom Font 1', sans-serif",
    cssVar: '--font-family-sans',
    icon: '🔤',
    translations: {
      EN: 'Custom Font 1',
      KU: 'فۆنتی ١',
      AR: 'خط ١',
      TR: 'Font 1'
    }
  },
  customFont2: {
    name: 'Font 2',
    family: "'Custom Font 2', sans-serif",
    cssVar: '--font-family-sans',
    icon: '🔤',
    translations: {
      EN: 'Custom Font 2',
      KU: 'فۆنتی ٢',
      AR: 'خط ٢',
      TR: 'Font 2'
    }
  },
  customFont3: {
    name: 'Font 3',
    family: "'Custom Font 3', sans-serif",
    cssVar: '--font-family-sans',
    icon: '🔤',
    translations: {
      EN: 'Custom Font 3',
      KU: 'فۆنتی ٣',
      AR: 'خط ٣',
      TR: 'Font 3'
    }
  },
  customFont4: {
    name: 'Font 4',
    family: "'Custom Font 4', sans-serif",
    cssVar: '--font-family-sans',
    icon: '🔤',
    translations: {
      EN: 'Custom Font 4',
      KU: 'فۆنتی ٤',
      AR: 'خط ٤',
      TR: 'Font 4'
    }
  },
  customFont5: {
    name: 'Font 5',
    family: "'Custom Font 5', sans-serif",
    cssVar: '--font-family-sans',
    icon: '🔤',
    translations: {
      EN: 'Custom Font 5',
      KU: 'فۆنتی ٥',
      AR: 'خط ٥',
      TR: 'Font 5'
    }
  },
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Helper function to generate hover, light, and dark variants from a base color
const generateColorVariants = (baseColor) => {
  // Convert hex to RGB
  const hex = baseColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Generate hover (darker)
  const hoverR = Math.max(0, Math.floor(r * 0.85));
  const hoverG = Math.max(0, Math.floor(g * 0.85));
  const hoverB = Math.max(0, Math.floor(b * 0.85));
  const hover = `#${hoverR.toString(16).padStart(2, '0')}${hoverG.toString(16).padStart(2, '0')}${hoverB.toString(16).padStart(2, '0')}`;

  // Generate light (very light tint)
  const lightR = Math.min(255, Math.floor(r + (255 - r) * 0.8));
  const lightG = Math.min(255, Math.floor(g + (255 - g) * 0.8));
  const lightB = Math.min(255, Math.floor(b + (255 - b) * 0.8));
  const light = `#${lightR.toString(16).padStart(2, '0')}${lightG.toString(16).padStart(2, '0')}${lightB.toString(16).padStart(2, '0')}`;

  // Generate dark (very dark)
  const darkR = Math.max(0, Math.floor(r * 0.3));
  const darkG = Math.max(0, Math.floor(g * 0.3));
  const darkB = Math.max(0, Math.floor(b * 0.3));
  const dark = `#${darkR.toString(16).padStart(2, '0')}${darkG.toString(16).padStart(2, '0')}${darkB.toString(16).padStart(2, '0')}`;

  return { hover, light, dark };
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Check localStorage first, then system preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme && THEMES[savedTheme]) {
      return savedTheme;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  const [primaryColor, setPrimaryColor] = useState(() => {
    // Check localStorage for saved primary color
    const savedColor = localStorage.getItem('primaryColor');
    if (savedColor) {
      try {
        const parsed = JSON.parse(savedColor);
        return parsed;
      } catch (e) {
        // If parsing fails, check if it's a preset color key
        if (PRIMARY_COLORS[savedColor]) {
          return PRIMARY_COLORS[savedColor];
        }
      }
    }
    // Default to emerald
    return PRIMARY_COLORS.emerald;
  });

  // eslint-disable-next-line no-unused-vars
  const [font, setFont] = useState(() => {
    // Check localStorage for saved font
    const savedFont = localStorage.getItem('selectedFont');
    if (savedFont && FONTS[savedFont]) {
      return savedFont;
    }
    // Default to customFont1
    return 'customFont1';
  });

  useEffect(() => {
    // Apply theme to root element
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    // Apply primary color to root element as CSS variables
    // Inline styles have higher specificity than CSS rules, so they will override theme colors
    const root = document.documentElement;
    root.style.setProperty('--color-primary', primaryColor.color);
    root.style.setProperty('--color-primary-hover', primaryColor.hover);
    root.style.setProperty('--color-primary-light', primaryColor.light);
    root.style.setProperty('--color-primary-dark', primaryColor.dark);
    
    // Convert hex color to rgba for text shadow (with 0.2 opacity)
    const hex = primaryColor.color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const textShadowColor = `rgba(${r}, ${g}, ${b}, 0.2)`;
    root.style.setProperty('--text-shadow-primary', textShadowColor);
    
    // Save to localStorage
    localStorage.setItem('primaryColor', JSON.stringify(primaryColor));
  }, [primaryColor]);

  useEffect(() => {
    // Apply font to root element as CSS variable
    const root = document.documentElement;
    const selectedFont = FONTS[font];
    if (selectedFont) {
      root.style.setProperty('--font-family', selectedFont.family);
      // Save to localStorage
      localStorage.setItem('selectedFont', font);
      // Dispatch custom event for other components
      window.dispatchEvent(new CustomEvent('fontChange', { detail: font }));
    }
  }, [font]);

  const changeTheme = (newTheme) => {
    if (THEMES[newTheme]) {
      setTheme(newTheme);
    }
  };

  const changePrimaryColor = (colorKeyOrHex) => {
    if (PRIMARY_COLORS[colorKeyOrHex]) {
      // It's a preset color
      setPrimaryColor(PRIMARY_COLORS[colorKeyOrHex]);
    } else if (colorKeyOrHex.startsWith('#')) {
      // It's a custom hex color
      const variants = generateColorVariants(colorKeyOrHex);
      setPrimaryColor({
        name: 'Custom',
        color: colorKeyOrHex,
        ...variants
      });
    }
  };

  const toggleTheme = () => {
    // For backward compatibility, toggle between light and dark
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const changeFont = (fontKey) => {
    if (FONTS[fontKey]) {
      setFont(fontKey);
    }
  };

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      changeTheme, 
      toggleTheme, 
      themes: THEMES,
      primaryColor,
      changePrimaryColor,
      primaryColors: PRIMARY_COLORS,
      font,
      changeFont,
      fonts: FONTS
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

